import { useMemo, useState } from 'react';

import type { PaymentPlanFormData } from '../types/payment';

const emptyForm: PaymentPlanFormData = {
  description: '',
  hidePaymentPlan: false,
  additionalInformation: '',
  scholarshipStatus: 'none',
  scholarshipTypeId: '',
  scholarshipReason: '',
  schoolDiscount: 0,
  observation: '',
  automaticGenerateInvoice: true,
  payAsCompany: false,
  companyName: '',
  cnpj: '',
  ie: '',
  ieFree: false,
  companyContactName: '',
  companyContactPhone: '',
  companyContactEmail: '',
  thirdPayment: false,
  thirdName: '',
  thirdDocument: '',
  thirdEmail: '',
  thirdPhone: '',
  valueRegistration: 0,
  totalValue: 0,
  paymentMethod: 'boleto',
  expirationDay: 15,
  discount: 0,
  numberOfParcels: 1,
  otherNumberParcels: 0,
  generateEnrollmentParcel: false,
  updatePlanParcels: false,
};

export function usePaymentPlanForm(_planId?: number) {
  const existingPlan = undefined;

  const [form, setForm] = useState<PaymentPlanFormData>(emptyForm);

  const updateField = <K extends keyof PaymentPlanFormData>(
    key: K,
    value: PaymentPlanFormData[K],
  ) => {
    setForm(prev => {
      const next = { ...prev, [key]: value };
      // Auto-enable thirdPayment when payAsCompany is checked
      if (key === 'payAsCompany' && value === true) {
        next.thirdPayment = true;
      }
      // Clear IE when ieFree is checked
      if (key === 'ieFree' && value === true) {
        next.ie = 'ISENTO';
      }
      return next;
    });
  };

  const monthlyValue = useMemo(() => {
    if (form.numberOfParcels <= 0) return 0;
    return (form.totalValue * (1 - form.discount / 100)) / form.numberOfParcels;
  }, [form.totalValue, form.discount, form.numberOfParcels]);

  const totalWithDiscount = useMemo(() => {
    return form.totalValue * (1 - form.discount / 100);
  }, [form.totalValue, form.discount]);

  const totalWithSchoolDiscount = useMemo(() => {
    return totalWithDiscount * (1 - form.schoolDiscount / 100);
  }, [totalWithDiscount, form.schoolDiscount]);

  const save = () => {};

  return {
    form,
    updateField,
    monthlyValue,
    totalWithDiscount,
    totalWithSchoolDiscount,
    save,
    isEditing: !!existingPlan,
    plan: existingPlan,
  } as const;
}
